<template>
    <card title="" :isFullPage="true">
        <data-table entity-name="erased disk" entity-name-plural="erased disks" :columns="columns" :rows="rows"
            :total-row-count="totalRowCount" selection-key="device_id" :action-in-progress="actionsInProgress > 0"
            @queryChanged="onQueryChanged">

            <template v-slot:actions="props">
                <import-action :selection-count="props.selectionCount" :disabled="actionsInProgress > 0"
                    :entity-name="props.entityName" :entity-name-plural="props.entityNamePlural"
                    import-resource="disk-devices"></import-action>
                <export-action v-bind="props" export-resource="disk-devices"></export-action>
                <delete-action v-if="isSuperUser || isStaffUser || isPartner || isManager" v-bind="props" @deleteAction="onDelete"></delete-action>
                <print-action v-bind="props" @printAction="onPrint"></print-action>
            </template>
        </data-table>
    </card>
</template>
<script>
import { mapGetters } from 'vuex';
import { notify } from '../services/notify';

import api from '../services/api';
import print from '../components/DataTable/print';
import DataTable from '../components/DataTable/DataTable.vue';
import DeleteAction from '../components/DataTable/Actions/DeleteAction.vue';
import ExportAction from '../components/DataTable/Actions/ExportAction.vue';
import ImportAction from '../components/DataTable/Actions/ImportAction.vue';
import PrintAction from '../components/DataTable/Actions/PrintAction.vue';

export default {
    name: 'DisksErased',
    components: {
        DataTable,
        DeleteAction,
        ExportAction,
        ImportAction,
        PrintAction,
    },
    data() {
        return {
            columns: [],
            baseColumns: [
                { key: 'details', type: 'action', action: this.viewDisk, actionIcon: 'hdd', visible: true },
                { key: 'host', type: 'action', action: this.viewComputer, actionIcon: 'desktop', disabled: row => row.hardware_id === null, visible: true },
                { key: 'created_at', type: 'datetime', visible: true },
                { key: 'updated_at', type: 'datetime' },
                { key: 'uploaded_at', type: 'datetime' },
                { key: 'software_version' },
                { key: 'report_id', label: 'Report ID' },
                { key: 'batch_name' },
                { key: 'created_by' },

                {
                    key: 'sector_size_bytes', type: 'number', type: 'category',
                    categories: [512, 520, 524, 528, 4096, 4112, 4160, 4224]
                },
                { key: 'capacity_display', proxy_key: 'capacity_bytes', label: 'Capacity', type: 'number', visible: true,
                    units: [
                        { label: 'MB', fromUser: v => v * 1e+6, toUser: v => v / 1e+6 },
                        { label: 'GB', fromUser: v => v * 1e+9, toUser: v => v / 1e+9 },
                        { label: 'TB', fromUser: v => v * 1e+12, toUser: v => v / 1e+12 },
                    ]
                },
                { key: 'hidden_areas_display', label: 'Hidden areas' },

                { key: 'system_serial', visible: true },
                { key: 'vendor', visible: true },
                { key: 'model', visible: true },
                { key: 'serial', visible: true },

                { key: 'rotation_rate' },
                { key: 'firmware_version' },

                {
                    key: 'medium_display', label: 'Medium', type: 'category',
                    categories: ['Rotating', 'Solid state', 'Hybrid']
                },
                {
                    key: 'transport', type: 'category',
                    categories: [{key: 'ata', label: 'ATA'}, {key: 'pata', label: 'PATA'}, {key: 'sata', label: 'SATA'}, {key: 'sas', label: 'SAS'},
                    {key: 'scsi', label: 'SCSI'}, {key: 'spi', label: 'SPI'}, {key: 'fc', label: 'Fibre Channel'}, {key: 'pcie', label: 'PCIe'},
                    {key: 'usb', label: 'USB'}],
                    formatter: (value) => {
                        if (value === 'pcie') return 'PCIe';
                        if (value === 'fc') return 'Fibre Channel';
                        return value.toUpperCase();
                    }
                },
                {
                    key: 'status_display', label: 'Status', type: 'category',
                    categories: ['Scanning', 'Identified', 'Preparing', 'Erasing', 'Verifying', 'Completed', 'Error']
                },
                {
                    key: 'type_display', label: 'Type', type: 'category',
                    categories: [ {key: 'ata', label: 'ATA'}, {key: 'scsi', label: 'SCSI'}, {key: 'nvme', label: 'NVMe' }, {key: 'mmc', label: 'MMC'}],
                    formatter: (value) => {
                        if (value === 'nvme') return 'NVMe';
                        return value.toUpperCase();
                    }
                },

                {
                    key: 'erase_algorithm_display', label: 'Erase method', proxy_key: "erase_algorithm", type: 'category',
                    categories: [
                        {key: "zero_1_pass", label: "Standard Overwrite 1-pass"},
                        {key: "random_1_pass", label: "Random Overwrite 1-pass"},
                        {key: "ieee_purge_or_clear", label: "IEEE 2883-2022 Purge or Clear"},
                        {key: "ieee_purge_only", label: "IEEE 2883-2022 Purge only"},
                        {key: "ieee_clear_only", label: "IEEE 2883-2022 Clear only"},
                        {key: "ieee_purge_plus_or_clear", label: "IEEE 2883-2022 Purge Plus or Clear"},
                        {key: "ieee_purge_plus_only", label: "IEEE 2883-2022 Purge Plus only"},
                        {key: "nist_purge_or_clear", label: "NIST 800-88-r1 Purge or Clear"},
                        {key: "nist_purge_only", label: "NIST 800-88-r1 Purge only"},
                        {key: "nist_clear_only", label: "NIST 800-88-r1 Clear only"},
                        {key: "nist_purge_plus_or_clear", label: "NIST 800-88-r1 Purge Plus or Clear"},
                        {key: "nist_purge_plus_only", label: "NIST 800-88-r1 Purge Plus only"},
                        {key: "dod_3_pass", label: "DoD 5220.22-M (E) 3-pass"},
                        {key: "dod_7_pass", label: "DoD 5220.22-M (ECE) 7-pass"},
                        {key: "navso_523926_mfm", label: "NAVSO P-5239-26 MFM 3-pass"},
                        {key: "navso_523926_rll", label: "NAVSO P-5239-26 RLL 3-pass"},
                        {key: "usarmy_ar_380_19", label: "US Army AR-380-19 3-pass"},
                        {key: "cesg_3_pass", label: "CESG CPA 3-pass"},
                        {key: "hmg_1_pass", label: "HMG IAS 5 Lower 1-pass"},
                        {key: "hmg_3_pass", label: "HMG IAS 5 Higher 3-pass"},
                        {key: "vsitr_7_pass", label: "VSITR 7-pass"},
                        {key: "gostr_1_pass", label: "GOST-R-50739-95 1-pass"},
                        {key: "gostr_2_pass", label: "GOST-R-50739-95 2-pass"},
                        {key: "do_not_erase", label: "Do not erase"}
                    ]
                },
                {
                    key: 'erase_result_display', label: 'Erase result', visible: true, type: 'category',
                    categories: ['Not erased', 'Pass', 'Fail', 'Canceled']
                },
                {
                    key: 'erase_level', visible: true, type: 'category',
                    categories: ['Clear', 'Purge']
                },
                { key: 'erase_start_time', label: 'Erase start', type: 'datetime' },
                { key: 'erase_finish_time', label: 'Erase finish', type: 'datetime' },
                { key: 'erase_duration_display', proxy_key: 'erase_duration_seconds', label: 'Erase duration', type: 'number' },

                { key: 'erase_read_bytes_per_second_display', proxy_key: 'erase_read_bytes_per_second', label: 'Erase read bytes/sec', type: 'number' },
                { key: 'erase_write_bytes_per_second_display', proxy_key: 'erase_write_bytes_per_second', label: 'Erase write bytes/sec', type: 'number' },
                { key: 'erase_verified_number_of_sectors', label: 'Erase verified sectors', type: 'number' },

                {
                    key: 'smart_overall_health_display', label: 'SMART health', type: 'category',
                    categories: ['Good', 'Bad']
                },
                { key: 'smart_short_test_result', label: 'SMART short test' },
                { key: 'power_on_hours', type: 'number' },
                { key: 'power_cycles', type: 'number' },
                { key: 'temperature_celsius', type: 'number' },
                { key: 'health', type: 'number' },
                { key: 'reallocated_sector_count', label: 'Reallocated before', type: 'number' },
                { key: 'reallocated_sector_count_after', label: 'Reallocated after', type: 'number' },
                {
                    key: 'extra', type: 'table', columns: [
                        { key: 'key' },
                        { key: 'value' }
                    ], visible: true
                },
                {
                    key: 'log', type: 'table', columns: [
                        { key: 'level', formatter: (value) => ['DEBUG', 'INFO', 'WARNING', 'ERROR'][value] },
                        { key: 'message' }
                    ], visible: true
                }
            ],
            rows: [],
            totalRowCount: 0,
            lastQuery: null,
            actionsInProgress: 0,
            savedTableName: null,
        }
    },
    async created() {
        await this.$store.cache.dispatch('user/fetchMe');
        if (this.isSuperUser || this.isStaffUser || this.isPartner) {
            this.columns = [{ key: 'organization.name', label: 'Organization', visible: true }, { key: 'group.name', label: 'Group', visible: true }, ...this.baseColumns];
        }
        else if (this.isManager) {
            this.columns = [{ key: 'group.name', label: 'Group', visible: true }, ...this.baseColumns];
        }
        else {
            this.columns = this.baseColumns;
        }
    },
    methods: {
        async refreshQuery() {
            if (this.lastQuery !== null) {
                await this.onQueryChanged(this.lastQuery);
            }
        },
        async onQueryChanged(query) {
            this.lastQuery = query;
            try {
                const response = await api.getWithParams('/disk-devices/', query);
                this.rows = response.data.data;
                this.totalRowCount = response.data.pagination.count;
            }
            catch (err) {
                this.rows = [];
                this.totalRowCount = 0;
                notify().error('Error fetching reports.');
            }
        },
        async onDelete(selectOrFilter) {
            //! TODO Only allow deleting loose disks?
            this.actionsInProgress++;
            try {
                const response = await api.delete('/disk-devices/', selectOrFilter);
                await this.refreshQuery();
            }
            catch (err) {
                notify().error('Error deleting reports.');
            }
            finally {
                this.actionsInProgress--;
            }
        },
        async onPrint(selectOrFilter, rowConverter) {
            this.actionsInProgress++;
            try {
                const response = await api.postJSON('/disk-devices/select', selectOrFilter);
                const rows = response.data;
                await print.pdf(rowConverter(rows));
            }
            catch (err) {
                notify().error('Error printing a report.');
            }
            finally {
                this.actionsInProgress--;
            }
        },
        async viewDisk(row) {
            this.$router.push(`/erased/disks/${row.device_id}`);
        },
        async viewComputer(row) {
            this.$router.push(`/erased/computers/${row.hardware_id}`);
        },
    },
    computed: {
        ...mapGetters('user', ['isSuperUser', 'isStaffUser', 'isPartner', 'isManager']),
    },
}
</script>
